import { TransformsList } from 'cio/utils/analytics/transforms';
import { ActionType } from 'cio/models/action';
import actionInfo from 'cio/utils/action-info';
import { capitalize, underscore } from '@ember/string';
import { generateFilteredTimeFrameData } from 'cio/utils/analytics/transforms/metrics';
import SegmentModel from 'cio/models/segment';
import { Periods } from 'cio/utils/metrics/common';
import { DELIVERY_METRICS_MEASURE_LABELS } from 'cio/utils/metrics/analysis';

const EDIT_TYPE_LABELS: Record<string, string> = {
  segment: 'Segment Changed',
  channel: 'Channel Changed',
  chart: 'Chart Changed',
  timeframe: 'Timeframe',
  groupBy: 'Group By',
  tab: 'Selected Tab Changed',
  sortBy: 'Sort By Changed',
  sort_direction: 'Sort Order Changed',
};

const SORT_DIRECTION_LABELS: Record<string, string> = {
  asc: 'Least',
  desc: 'Most',
};

const SORT_BY_LABELS: Record<string, string> = {
  opened: 'Opened',
  clicked: 'Clicked',
  converted: 'Converted',
  most_recent: 'Most Recent',
  longest_running: 'Longest Running',
};

const GROUP_BY_LABELS: Record<string, string> = {
  [Periods.DAYS]: 'By Day',
  [Periods.WEEKS]: 'By Week',
  [Periods.MONTHS]: 'By Month',
};

const CARD_LABELS: Record<string, string> = {
  segments: 'Segments',
  leaderboard: 'Leaderboard',
  performance: 'Performance',
  delivery: 'Delivery',
  tests: 'Tests',
  links: 'Links',
};

const DESTINATION_LABELS: Record<string, string> = {
  segment: 'Segment',
  campaign: 'Campaign',
  newsletter: 'Newsletter',
  broadcast: 'Broadcast',
  test: 'Test',
  analysis: 'Analysis',
};

const Transforms: TransformsList = {
  base(data) {
    return {
      'Message Type': actionInfo(`${data.type}_action` as ActionType).analyticsName,
    };
  },

  navigateAway(data) {
    let destination = null;

    if (data.destination) {
      destination = DESTINATION_LABELS[data.destination];
    } else if (data?.route?.name) {
      destination = data.route.name;
    }

    let eventData: Record<string, any> = {
      'Page View Time': data.time,
      Destination: destination,
      Card: CARD_LABELS[data.card] ?? null,
      Channel: null,
      Metric: null,
      'Sort Order': null,
    };

    if (data?.metric) {
      eventData.Metric = (DELIVERY_METRICS_MEASURE_LABELS as any)[underscore(data.metric)];
    }

    if (data?.channel) {
      eventData.Channel = actionInfo(`${data.channel}_action` as ActionType).analyticsName;
    }

    if (data?.sortDirection) {
      eventData['Sort Order'] = SORT_DIRECTION_LABELS[data.sortDirection];
    }

    return eventData;
  },

  chartChanged(data) {
    let eventData: Record<string, any> = {
      Card: CARD_LABELS[data.card],
      'Edit Type': EDIT_TYPE_LABELS[data.editType],
      'Segment ID': null,
      'Segment Name': null,
      Metric: null,
      Channel: null,
    };

    if (data?.model instanceof SegmentModel) {
      eventData['Segment ID'] = data.model.id;
      eventData['Segment Name'] = data.model.name;
    }

    if (data?.metric) {
      eventData.Metric = (DELIVERY_METRICS_MEASURE_LABELS as any)[underscore(data.metric)];
    }

    if (data?.channel) {
      eventData.Channel = actionInfo(`${data.channel}_action` as ActionType).analyticsName;
    }

    return eventData;
  },

  chartHover(data) {
    let eventData: Record<string, any> = {
      'Time of Hover': data.time,
      'Segment ID': null,
      'Segment Name': null,
      Metric: null,
      Channel: null,
    };

    if (data?.model instanceof SegmentModel) {
      eventData['Segment ID'] = data.model.id;
      eventData['Segment Name'] = data.model.name;
    }

    if (data?.metric) {
      eventData.Metric = (DELIVERY_METRICS_MEASURE_LABELS as any)[underscore(data.metric)];
    }

    if (data?.channel) {
      eventData.Channel = actionInfo(`${data.channel}_action` as ActionType).analyticsName;
    }

    return eventData;
  },

  timeframeFiltersChanged(data) {
    return {
      ...generateFilteredTimeFrameData(data),
      'Group By Value': GROUP_BY_LABELS[data.groupBy],
      'Edit Type': EDIT_TYPE_LABELS[data.editType],
    };
  },

  leaderboardCardDisplayChanged(data) {
    return {
      'Selected Tab': capitalize(data.tab),
      'Sort Direction': SORT_DIRECTION_LABELS[data.sortDirection],
      'Edit Type': EDIT_TYPE_LABELS[data.editType],
    };
  },

  testsCardDisplayChanged(data) {
    return {
      'Selected Tab': capitalize(data.tab),
      'Sort By': SORT_BY_LABELS[data.sortBy],
      'Edit Type': EDIT_TYPE_LABELS[data.editType],
    };
  },

  channel(data) {
    return {
      Channel: actionInfo(`${data.channel}_action` as ActionType).analyticsName,
    };
  },
};

export default Transforms;
